import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { InlineNotification } from '@dealstack/frontend/shared/components/InlineNotification/InlineNotification';
import { validateTin as validateTinService } from '@dealstack/frontend/shared/dataExchange/services/validation.service.ts';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme.ts';
import { groupedSelectDividerStyles } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import { COUNTRIES_NAME_CODE_GROUPED_OPTIONS } from '@dealstack/shared/lib/countries/countriesData.ts';
import { Button, Flex, Loader } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import { memo, type ReactNode } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { Select, TextInput } from 'react-hook-form-mantine';
import { TaxDetailsHeader } from '@dealstack/frontend/shared/components/SingleTaxDetails/TaxDetailsHeader.tsx';

interface Props {
	initialTaxResidency?: string;
	initialTin?: string;
	taxResidencyFieldName: string;
	tinFieldName: string;
	disabled?: boolean;
	withHeader?: boolean;
	headerInfoText?: string;
	rightSection?: ReactNode;
}

export const VALIDATE_TIN_MUTATION_KEY = 'validateTin';

const SingleTaxDetails = ({
	initialTaxResidency,
	initialTin,
	taxResidencyFieldName,
	tinFieldName,
	disabled = false,
	withHeader = true,
	headerInfoText,
	rightSection,
}: Props) => {
	const tCommonUi = useTranslationCommonUi();

	const validateTinRequest = useMutation({
		mutationFn: validateTinService,
		mutationKey: [VALIDATE_TIN_MUTATION_KEY],
	});

	const form = useFormContext();

	const taxResidencyField = useController({ name: taxResidencyFieldName });
	const tinField = useController({ name: tinFieldName });

	const validateTin = () => {
		if (!taxResidencyField.field.value || !tinField.field.value) {
			return;
		}

		validateTinRequest.mutate(
			{ tin: tinField.field.value, countryCode: taxResidencyField.field.value },
			{
				onSuccess: (result) => {
					if (result?.isValid === false) {
						form.setError(tinFieldName, {
							type: 'manual',
							message: tCommonUi('single_tax_details.invalid'),
						});
					}
				},
			}
		);
	};

	const onFieldsBlur = () => {
		const isTaxResidencyChanged = initialTaxResidency !== taxResidencyField.field.value;
		const isTinChanged = initialTin !== tinField.field.value;

		if (isTaxResidencyChanged || isTinChanged) {
			validateTin();
		}
	};

	const showInvalidMessage =
		!validateTinRequest.isPending && validateTinRequest.data?.isValid === false;

	return (
		<div>
			{withHeader && <TaxDetailsHeader infoText={headerInfoText} />}

			<Flex gap="0.5rem" mt="1rem" mb="1rem">
				<Select
					name={taxResidencyFieldName}
					label={tCommonUi('single_tax_details.tax_residency')}
					placeholder={tCommonUi('single_tax_details.tax_residency')}
					data={COUNTRIES_NAME_CODE_GROUPED_OPTIONS}
					onBlur={onFieldsBlur}
					searchable
					size="md"
					disabled={disabled}
					sx={{ flex: 1 }}
					styles={groupedSelectDividerStyles}
				/>

				<TextInput
					name={tinFieldName}
					placeholder={tCommonUi('single_tax_details.tin')}
					label={tCommonUi('single_tax_details.tin')}
					size="md"
					disabled={disabled || !taxResidencyField.field.value}
					onBlur={onFieldsBlur}
					sx={{
						flex: 3,
						'.mantine-InputWrapper-error': {
							display: showInvalidMessage ? 'none' : 'block',
						},
					}}
					rightSection={
						(validateTinRequest.isPending && <Loader size="xs" variant="oval" />) ||
						(!tinField.fieldState.error && validateTinRequest.data?.isValid && (
							<Icon
								variant="checkmark-circle-contained-filled"
								color={defaultTheme.v3.colors.teal[8]}
								size="1.35rem"
							/>
						))
					}
				/>
				{rightSection && (
					<Flex align="flex-start" mt="1.6rem">
						{rightSection}
					</Flex>
				)}
			</Flex>

			{showInvalidMessage && (
				<InlineNotification
					variant="error"
					title={tCommonUi('single_tax_details.error.title')}
					description={tCommonUi('single_tax_details.error.description')}
					rightSection={
						<Button variant="link-color" onClick={validateTin}>
							{tCommonUi('single_tax_details.error.try_again')}
						</Button>
					}
				/>
			)}
		</div>
	);
};

const Memoized = memo(SingleTaxDetails);

export { Memoized as SingleTaxDetails };
